import { useMemo } from 'react';
import { Badge, Checkbox, Group, Stack, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { shipToStepMessages } from './ship-to-step-messages';
import styles from './ShipToStep.module.css';
import { AddressForm } from '../../../../address/address-form';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { REQUIRED_SHIP_TO_ADDRESS_FIELDS, sliceFieldErrors } from '../../wizard-step-validation';
import { ServicesGrid } from '../services-grid/ServicesGrid';

import type { AddressFormReferenceData } from '../../../../address/address-form';
import type { Customer } from '../../../../types/customer';
import type { LtlAddress } from '../../../../types/ltl';
import type { State } from '../../../../types/reference-data';
import type { FreightBookingFormState, WizardDeliveryServices } from '../../freight-booking-wizard-types';
import type { ServicesGridField } from '../services-grid/ServicesGrid';
import type { MessageDescriptor } from 'react-intl';

const DELIVERY_FIELDS: ServicesGridField<WizardDeliveryServices>[] = [
  { key: 'isInsideDelivery', label: freightFormMessages.accessorialInsideDelivery },
  { key: 'isConstructionSiteDelivery', label: freightFormMessages.accessorialConstructionSiteDelivery },
  { key: 'isLiftgateDelivery', label: freightFormMessages.accessorialLiftgateDelivery },
  { key: 'isNotifyBeforeDelivery', label: freightFormMessages.accessorialNotifyBeforeDelivery },
  { key: 'isResidentialDelivery', label: freightFormMessages.accessorialResidentialDelivery },
  { key: 'isHoldAtTerminal', label: freightFormMessages.accessorialHoldShipmentAtTerminal },
  { key: 'isGroceryConsolidationDelivery', label: freightFormMessages.accessorialGroceryConsolidationDelivery },
  { key: 'isAppointmentDelivery', label: freightFormMessages.accessorialAppointmentDelivery },
  { key: 'isLimitedAccess', label: freightFormMessages.accessorialLimitedAccess },
  { key: 'isTradeshowDelivery', label: freightFormMessages.accessorialTradeshowDelivery },
];

const DELIVERY_FIELD_BY_KEY: Record<keyof WizardDeliveryServices, MessageDescriptor> = DELIVERY_FIELDS.reduce(
  (accumulator, field) => ({ ...accumulator, [field.key]: field.label }),
  {} as Record<keyof WizardDeliveryServices, MessageDescriptor>,
);

// country is US-only so the picker is hidden. Note: the country field has id 'country',
// not 'countryCode' — matching schema-generator filterHiddenFields which uses field.id.
const HIDDEN_ADDRESS_FIELDS = ['country'] as const;

// Static reference data — the wizard is domestic US only so countries and
// loading flags are not needed.
const buildReferenceData = (states: State[]): AddressFormReferenceData => ({
  countries: [],
  states,
  isLoadingCountries: false,
  isLoadingStates: false,
  sellerHomeCountry: 'US',
});

export type ShipToStepProps = {
  formState: FreightBookingFormState;
  onFormChange: (next: FreightBookingFormState) => void;
  onCustomerSelect?: (customer: Customer) => void;
  states: State[];
  /** Formatted inline error messages keyed by field path (e.g. `'destinationAddress.city'`). */
  fieldErrors?: Record<string, string>;
};

const ADDRESS_PREFIX = 'destinationAddress';

export const ShipToStep = ({ formState, onFormChange, onCustomerSelect, states, fieldErrors }: ShipToStepProps) => {
  const intl = useIntl();

  const destination = formState.destinationAddress;

  const handleAddressChange = (next: Partial<LtlAddress>) =>
    onFormChange({ ...formState, destinationAddress: { ...destination, ...next } });

  const handleDeliveryServicesChange = (next: WizardDeliveryServices) => onFormChange({ ...formState, deliveryServices: next });

  const handleTradeshowField = (field: keyof FreightBookingFormState['tradeshow'], value: string) =>
    onFormChange({ ...formState, tradeshow: { ...formState.tradeshow, [field]: value } });

  const referenceData = useMemo(() => buildReferenceData(states), [states]);

  return (
    <Stack gap='md'>
      <AddressForm
        addressType='shipTo'
        countryCode='US'
        errors={fieldErrors ? sliceFieldErrors(fieldErrors, ADDRESS_PREFIX) : undefined}
        hiddenFields={HIDDEN_ADDRESS_FIELDS}
        onChange={(next) => handleAddressChange(next as Partial<LtlAddress>)}
        onCustomerSelect={onCustomerSelect}
        referenceData={referenceData}
        requiredFields={REQUIRED_SHIP_TO_ADDRESS_FIELDS}
        showAddressLine3={false}
        value={destination}
      />

      <Checkbox
        checked={formState.shouldSaveAsNewCustomer}
        label={intl.formatMessage(shipToStepMessages.saveAsNewCustomer)}
        onChange={(event) => onFormChange({ ...formState, shouldSaveAsNewCustomer: event.currentTarget.checked })}
      />

      <span className={styles.servicesHeader}>{intl.formatMessage(shipToStepMessages.deliveryServicesHeader)}</span>
      <ServicesGrid fields={DELIVERY_FIELDS} onChange={handleDeliveryServicesChange} value={formState.deliveryServices} />

      {formState.deliveryServices.isTradeshowDelivery && (
        <Group grow>
          <TextInput
            data-testid='tradeshow-name'
            label={intl.formatMessage(shipToStepMessages.tradeshowNameLabel)}
            onChange={(event) => handleTradeshowField('name', event.currentTarget.value)}
            value={formState.tradeshow.name}
          />
          <TextInput
            data-testid='tradeshow-booth'
            label={intl.formatMessage(shipToStepMessages.boothNumberLabel)}
            onChange={(event) => handleTradeshowField('boothNumber', event.currentTarget.value)}
            value={formState.tradeshow.boothNumber}
          />
        </Group>
      )}
    </Stack>
  );
};

export type ShipToStepSummaryProps = {
  formState: FreightBookingFormState;
};

export const ShipToStepSummary = ({ formState }: ShipToStepSummaryProps) => {
  const intl = useIntl();

  const destination = formState.destinationAddress;
  const cityStateZip = [destination.city, destination.state, destination.postalCode].filter(Boolean).join(', ');

  const activeServices = DELIVERY_FIELDS.filter((field) => formState.deliveryServices[field.key]).map((field) =>
    intl.formatMessage(DELIVERY_FIELD_BY_KEY[field.key]),
  );

  const hasTradeshow = formState.deliveryServices.isTradeshowDelivery && (formState.tradeshow.name || formState.tradeshow.boothNumber);

  const tradeshowLine = (() => {
    const { name, boothNumber } = formState.tradeshow;
    if (name && boothNumber) {
      return intl.formatMessage(shipToStepMessages.tradeshowLine, { name, booth: boothNumber });
    }
    if (name) {
      return intl.formatMessage(shipToStepMessages.tradeshowNameOnly, { name });
    }
    if (boothNumber) {
      return intl.formatMessage(shipToStepMessages.tradeshowBoothOnly, { booth: boothNumber });
    }
    return null;
  })();

  return (
    <div className={styles.summary}>
      <div className={styles.summarySection}>
        <span className={styles.sectionLabel}>{intl.formatMessage(shipToStepMessages.destinationSectionLabel)}</span>
        {destination.name && <span className={styles.summaryPrimary}>{destination.name}</span>}
        {destination.company && <span className={styles.summaryPrimary}>{destination.company}</span>}
        {destination.line1 && <span className={styles.summarySecondary}>{destination.line1}</span>}
        {cityStateZip && <span className={styles.summarySecondary}>{cityStateZip}</span>}
        {destination.phone && <span className={styles.summarySecondary}>{destination.phone}</span>}
      </div>

      {activeServices.length > 0 && (
        <div className={styles.summarySection}>
          <span className={styles.sectionLabel}>{intl.formatMessage(shipToStepMessages.deliveryServicesHeader)}</span>
          <div className={styles.tags}>
            {activeServices.map((service) => (
              <Badge color='light' key={service} size='lg' variant='filled'>
                {service}
              </Badge>
            ))}
          </div>
          {hasTradeshow && tradeshowLine && <span className={styles.summarySecondary}>{tradeshowLine}</span>}
        </div>
      )}
    </div>
  );
};
